import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import type { AddSubscriptionFormSchemaType } from "@/widgets/offer-cargo-subscription-management/model";
import { addSubscriptionFormSchema } from "@/widgets/offer-cargo-subscription-management/model";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import { Controller, useForm } from "react-hook-form";

type Props = {
  companies: FreightMatching.schemas.Company[] | null;
  onSubmit: (data: AddSubscriptionFormSchemaType) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
};

export const AddSubscriptionForm = ({
  companies,
  onSubmit,
  onCancel,
  isSubmitting,
}: Props) => {
  const { control, handleSubmit } = useForm<AddSubscriptionFormSchemaType>({
    resolver: zodResolver(addSubscriptionFormSchema),
    defaultValues: {
      companyId: 0,
    },
  });

  const handleFormSubmit = async (data: AddSubscriptionFormSchemaType) => {
    await onSubmit(data);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{
        p: 2,
        border: 1,
        borderColor: "grey.300",
        borderRadius: 1,
        bgcolor: "grey.50",
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight={600}>
          新しいサブスクリプションを追加
        </Typography>

        <Controller
          name="companyId"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={fieldState.invalid} size="small">
              {companies === null ? (
                <CircularProgress size={20} />
              ) : (
                <Autocomplete
                  {...field}
                  value={
                    companies.find((company) => company.id === field.value) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    field.onChange(newValue?.id || 0);
                  }}
                  options={companies}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={fieldState.invalid}
                      placeholder="会社を選択してください"
                      label="会社名"
                    />
                  )}
                />
              )}
              {fieldState.error && (
                <FormHelperText>{fieldState.error.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <BaseButton
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            キャンセル
          </BaseButton>
          <BaseButton
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={16} />
              ) : (
                <BaseIcon name="plus" />
              )
            }
          >
            追加
          </BaseButton>
        </Stack>
      </Stack>
    </Box>
  );
};
